// src/EPFCalculator.js
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';

// Register necessary components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const EPFCalculator = () => {
  const [monthlySalary, setMonthlySalary] = useState(50000);
  const [age, setAge] = useState(30);
  const [contribution, setContribution] = useState(12);
  const [annualIncrease, setAnnualIncrease] = useState(5);
  const [rateOfInterest, setRateOfInterest] = useState(8.5);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [maturityValue, setMaturityValue] = useState(0);

  const retirementAge = 58;

  // const calculateMaturity = () => {
  //   const yearsOfContribution = retirementAge - age;
  //   let currentSalary = monthlySalary;
  //   let totalInvestment = 0;
  //   let maturityValue = 0;

  //   for (let i = 0; i < yearsOfContribution; i++) {
  //     // let yearlyContribution = (currentSalary * (contribution / 100)) * 12;
  //     let yearlyContribution = (currentSalary * (contribution / 100)) * 12 * 2;
  //     totalInvestment += yearlyContribution;
  //     maturityValue = (maturityValue + yearlyContribution) * (1 + rateOfInterest / 100);
  //     currentSalary += currentSalary * (annualIncrease / 100);
  //   }

  //   let totalInterest = maturityValue - totalInvestment;

  //   setTotalInvestment(totalInvestment);
  //   setTotalInterest(totalInterest);
  //   setMaturityValue(maturityValue.toFixed(2));
  // };
  const calculateMaturity = () => {
    const months = (retirementAge - age) * 12;
    let currentSalary = monthlySalary;
    let totalInvestment = 0;
    let maturityValue = 0;
  
    const totalContributionPercent = contribution * 2; // Employee + Employer
    const monthlyInterestRate = rateOfInterest / 12 / 100;
  
    for (let month = 1; month <= months; month++) {
      const monthlyContribution = currentSalary * (totalContributionPercent / 100);
      totalInvestment += monthlyContribution;
      maturityValue = (maturityValue + monthlyContribution) * (1 + monthlyInterestRate);
  
      // Increase salary annually
      if (month % 12 === 0) {
        currentSalary += currentSalary * (annualIncrease / 100);
      }
    }
  
    const totalInterest = maturityValue - totalInvestment;
  
    setTotalInvestment(totalInvestment);
    setTotalInterest(totalInterest);
    // setMaturityValue(maturityValue.toFixed(2));
    setMaturityValue(maturityValue);
  };
  
  const handleMonthlySalaryChange = (e) => {
    const value = e.target.value;
    setMonthlySalary(value === '' ? 0 : parseInt(value));
  };

  const handleAgeChange = (e) => {
    const value = e.target.value;
    setAge(value === '' ? 0 : parseInt(value));
  };

  const handleContributionChange = (e) => {
    const value = e.target.value;
    setContribution(value === '' ? 0 : parseFloat(value));
  };

  const handleAnnualIncreaseChange = (e) => {
    const value = e.target.value;
    setAnnualIncrease(value === '' ? 0 : parseFloat(value));
  };

  const handleRateOfInterestChange = (e) => {
    const value = e.target.value;
    setRateOfInterest(value === '' ? 0 : parseFloat(value));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const data = {
    labels: ['Total Investment', 'Total Interest', 'Maturity Value'],
    datasets: [
      {
        label: 'EPF Breakdown',
        data: [totalInvestment, totalInterest, maturityValue],
        backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is an EPF calculator?',
        answer: 'An EPF calculator is an online tool that estimates the total amount of money an employee will accumulate in their EPF account at the time of retirement.',
      },
      {
        question: 'Is EPF calculator result accurate?',
        answer: 'The results are close approximations and can vary slightly due to changes in salary, interest rates, or contributions.',
      },
      {
        question: 'What is the current interest rate on EPF?',
        answer: 'For FY 2024-25, the EPF interest rate is 8.15% per annum.',
      },
      {
        question: 'Can I calculate EPF manually?',
        answer: 'Yes, but it involves complex compounding interest calculations. Using a calculator is much easier and error-free.',
      },
      {
        question: 'Is the employer’s full 12% contribution credited to EPF?',
        answer: 'No, only 3.67% goes to EPF. The rest goes to EPS and other funds.',
      },
      {
        question: 'Does the EPF calculator include VPF contributions?',
        answer: 'Some calculators have that option. If not, VPF must be calculated separately.',
      },
      {
        question: 'Can I use the calculator if I’m a freelancer or self-employed?',
        answer: 'EPF is for salaried individuals. Freelancers can’t use it unless they’re part of a company payroll.',
      },
      {
        question: 'Is EPF better than PPF?',
        answer: 'EPF offers a higher interest rate but is limited to salaried employees. PPF is open to all.',
      },
      {
        question: 'What is the role of UAN in EPF?',
        answer: 'UAN allows portability of EPF accounts and easy tracking of contributions.',
      },
      {
        question: 'Can I withdraw from EPF before retirement?',
        answer: 'Yes, under specific conditions such as home purchase, medical emergencies, or unemployment.',
      },
      {
        question: 'Does salary hike affect EPF maturity?',
        answer: 'Yes, as contributions are based on salary. A higher salary increases contributions and hence the corpus.',
      },
      {
        question: 'Is interest on EPF calculated monthly?',
        answer: 'Yes, but it is credited to your account at the end of the financial year.',
      },
      {
        question: 'Can I increase my EPF contribution?',
        answer: 'Yes, through VPF (Voluntary Provident Fund).',
      },
      {
        question: 'Can the EPF calculator be used to plan early retirement?',
        answer: 'Yes, by adjusting the retirement age in the calculator.',
      },
      {
        question: 'Is EPF a good option for long-term saving?',
        answer: 'Yes, it’s a safe, government-backed scheme with tax benefits and steady interest growth.',
      },
    ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
    <Helmet>
         <title>EPF Calculator – Provident Fund Balance & Interest Estimator</title>
        <meta  name='description' content='Calculate your PF contributions, interest, and total balance with our EPF calculator. Track your retirement savings.'/>
        <meta name='keywords' content='monthly pf calculator, pf interest calculator, calculate epf interest, calculate epf online, epf interest calculator online pf calculator, pf calculation for salary' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/pf-epf-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
</Helmet>
<FAQSchema faqs={faqs} />
<nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">EPF Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className='py-4'>
    <div className="container mx-auto p-4 shadow-xl rounded-md">
      <h1 className="text-2xl font-bold mb-4">EPF Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Monthly Salary (Basic + DA)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={monthlySalary}
                onChange={handleMonthlySalaryChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {monthlySalary.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="200000"
              value={monthlySalary}
              onChange={(e) => setMonthlySalary(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Your Age</label>
            <div className="flex items-center">
              <input
                type="number"
                min="18"
                max="58"
                value={age}
                onChange={handleAgeChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{age} Yr</span>
            </div>
            <input
              type="range"
              min="18"
              max="58"
              value={age}
              onChange={(e) => setAge(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Your Contribution to EPF (%)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={contribution}
                onChange={handleContributionChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{contribution} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={contribution}
              onChange={(e) => setContribution(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Annual Increase in Salary (%)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={annualIncrease}
                onChange={handleAnnualIncreaseChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{annualIncrease} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={annualIncrease}
              onChange={(e) => setAnnualIncrease(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (%)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="15"
                step="0.1"
                value={rateOfInterest}
                onChange={handleRateOfInterestChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{rateOfInterest} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="15"
              step="0.1"
              value={rateOfInterest}
              onChange={(e) => setRateOfInterest(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateMaturity}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          {/* <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <div className="bg-white shadow rounded p-4 text-center">
              <p className="text-gray-700">You will have accumulated</p>
              <p className="text-3xl font-bold text-gray-800">{formatCurrency(maturityValue)}</p>
              <p className="text-gray-700">by the time you retire</p>
            </div>
          </div> */}
          <div>
            {/* <h2 className="text-xl font-bold">Breakdown</h2> */}
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
            
          </div>
          <div className="mb-4 mt-6 lg:ml-6">
            <h2 className="text-xl font-bold ml-14">Results</h2>
            <div className="bg-white shadow-md rounded p-4 text-center">
              <p className="text-gray-700">You will have accumulated</p>
              <p className="text-3xl font-bold text-gray-800">{formatCurrency(maturityValue)}</p>
              <p className="text-gray-700">by the time you retire</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is the Employee Provident Fund (EPF)?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The Employee Provident Fund (EPF) is a government-backed retirement savings scheme designed specifically for salaried individuals in India, primarily those working in the private sector. Managed by the Employees' Provident Fund Organisation (EPFO), the EPF acts as a long-term investment plan that encourages employees to save a portion of their income throughout their employment tenure. These savings, along with interest, can be withdrawn upon retirement or under certain eligible conditions.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            While government employees often receive pensions after retirement, EPF serves as the primary financial cushion for private sector workers. The scheme is not only mandatory for companies with 20 or more employees but also beneficial due to its tax-exempt nature under Section 80C of the Income Tax Act.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does EPF Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              When you start working for a company registered under EPFO, both you and your employer contribute a fixed percentage (typically 12%) of your basic salary and dearness allowance into your EPF account. This creates a corpus of savings over time. Here's how the mechanism operates:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Employee Contribution:</strong> 12% of basic salary + DA</li>
              <li><strong>Employer Contribution:</strong> 12% of basic salary + DA (with 3.67% going to EPF and the rest towards Employee Pension Scheme and other administrative charges)</li>
              <li><strong>Interest Rate:</strong> The EPF interest rate is declared by the government annually. As of FY 2024-25, the rate is 8.15% per annum.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Every employee is allotted a Universal Account Number (UAN), which stays the same even if they switch employers. This UAN helps employees track their contributions and balance across different jobs without disruption.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use an EPF Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The EPF calculator is an online tool designed to simplify the estimation of your EPF corpus by calculating your total contributions and interest over time. Here are the key reasons why it’s an essential tool for financial planning:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Track Your Retirement Savings:</strong> Understand how your EPF contributions are compounding over the years.</li>
              <li><strong>Avoid Manual Errors:</strong> Manual calculations can be time-consuming and error-prone. EPF calculators automate the process with accurate projections.</li>
              <li><strong>Plan Better:</strong> By getting an estimate of your maturity amount, you can align your retirement planning and investment strategy accordingly.</li>
              <li><strong>Stay Updated:</strong> Easily factor in any changes to interest rates or salary hikes and see their impact in real-time.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does an EPF Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Most EPF calculators follow a basic formula to compute the final maturity amount. The tool requires you to enter certain data points, and based on compounding interest and monthly contributions, it displays the projected EPF balance.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula Used:</strong> The EPF maturity amount is calculated based on:
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              EPF Maturity = Sum of (Employee Contribution + Employer Contribution + Compound Interest)
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator usually considers:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Monthly employee contribution (12% of basic + DA)</li>
              <li>Monthly employer contribution (3.67% of basic + DA)</li>
              <li>Annual interest rate</li>
              <li>Duration of employment (in years)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example Calculation:</strong> Suppose an individual earns a basic salary of Rs. 30,000 per month:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Employee contribution = Rs. 3,600/month</li>
              <li>Employer contribution = Rs. 1,101/month (EPF share)</li>
              <li>Total contribution per year = Rs. 56,412</li>
              <li>At 8.15% interest, over 20 years, the maturity value will be approx. Rs. 25+ lakhs (depending on increment and compounding frequency).</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Step-by-Step Guide: How to Use the EPF Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using an EPF calculator is simple and intuitive. Follow these steps to get your estimated maturity amount:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Enter Your Basic Monthly Salary:</strong> This includes only the base pay and dearness allowance.</li>
              <li><strong>Provide Your Current Age:</strong> Helps estimate the number of working years until retirement.</li>
              <li><strong>Current EPF Balance (if any):</strong> Optional, but useful for ongoing EPF contributors.</li>
              <li><strong>Expected Annual Salary Increase:</strong> Optional, but useful for a more accurate future projection.</li>
              <li><strong>Click on Calculate:</strong> The tool will process your input and show the estimated EPF maturity amount, including principal and interest.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Key Features and Benefits of EPF Calculators
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Simplicity:</strong> Just input your salary, age, and contribution details to get instant results.</li>
              <li><strong>Precision:</strong> Uses compound interest formulas to give close-to-accurate maturity estimates.</li>
              <li><strong>Flexibility:</strong> Can accommodate changes in salary, interest rate, and contribution amount.</li>
              <li><strong>Time-Saving:</strong> Avoids manual math and gives you results in seconds.</li>
              <li><strong>Goal-Based Planning:</strong> Helps you decide if your EPF corpus is enough for retirement or if you need to supplement it with other investments.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tax Benefits of EPF
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              EPF is an Exempt-Exempt-Exempt (EEE) investment, meaning:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Exempt at Investment:</strong> Contributions qualify for deduction under Section 80C.</li>
              <li><strong>Exempt on Accumulation:</strong> Interest earned is tax-free.</li>
              <li><strong>Exempt on Withdrawal:</strong> If withdrawn after 5 continuous years of service, the entire amount is tax-free.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Voluntary Provident Fund (VPF) – An Extension of EPF
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Employees can choose to contribute more than the mandatory 12% of their salary under the Voluntary Provident Fund (VPF). This extra contribution earns the same interest as EPF and is also tax-exempt under Section 80C, making it a great option for risk-averse investors.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Considerations While Using an EPF Calculator
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Always input your basic salary and not gross salary.</li>
              <li>Remember that interest is calculated monthly but credited annually.</li>
              <li>Salaries and interest rates change, so update your inputs regularly.</li>
              <li>Factor in any breaks in service or early withdrawals.</li>
            </ul>
          </div>

          <h1 className="text-xl font-medium text-gray-700 mt-4">
            “Looking to plan your retirement? Track your Employee Provident Fund with TheFinMaster and secure your financial future!”
          </h1>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
            <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>

        </div>

      </div>

      <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default EPFCalculator;
